import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Package, Clock, Tag, Truck, Building2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SEO from "../components/SEO";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const deliveryMonths = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const quantityRanges = [
  "100-250 units",
  "250-500 units", 
  "500-1000 units",
  "1000+ units"
];

const labelOptions = [
  "Blank labels",
  "Cotton Candy Girl branded",
  "Private label (my branding)"
];

export default function WholesalePage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    city: "",
    estimated_quantity: "",
    desired_flavors: "",
    label_option: "",
    delivery_month: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/inquiries/wholesale`, formData);
      
      toast.success("Inquiry submitted!", {
        description: "We'll review your request and get back to you within 1-2 business days.",
      });

      setFormData({
        business_name: "",
        contact_name: "",
        email: "",
        phone: "",
        city: "",
        estimated_quantity: "",
        desired_flavors: "",
        label_option: "",
        delivery_month: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to submit inquiry", {
        description: "Please try again or email us directly at shea@cottoncandygirl.co",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-pink-50 to-white" data-testid="wholesale-page">
      <SEO 
        title="Wholesale Cotton Candy"
        description="Wholesale cotton candy for retailers, resorts, and entertainment venues. 32oz tamper-evident containers, private-label options, non-toxic naturally UV-activated Birthday Cake. Serving Las Vegas and Boise."
        keywords="wholesale cotton candy, bulk cotton candy, private label cotton candy, retail cotton candy, Las Vegas wholesale"
        canonical="/wholesale"
      />
      
      {/* Hero */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block bg-[#FF9EAA] text-white text-sm font-bold px-4 py-2 rounded-full mb-6">
            WHOLESALE PROGRAM
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#4A4A4A] mb-6">
            Wholesale <span className="gradient-text">Cotton Candy</span>
          </h1>
          <p className="text-lg md:text-xl text-[#888888] leading-relaxed max-w-2xl mx-auto">
            Premium shelf-ready cotton candy for retailers, resorts, entertainment venues, and specialty businesses.
          </p>
        </div>
      </section>

      {/* Product Specs */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-[#4A4A4A] mb-8 text-center">Product Specifications</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-md" data-testid="spec-container">
              <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center mb-4">
                <Package className="text-[#FF9EAA]" size={24} />
              </div>
              <h3 className="font-bold text-[#4A4A4A] mb-2">Container Size</h3>
              <p className="text-[#888888]">32-ounce clear, tamper-evident containers</p>
              <p className="text-sm text-[#AAAAAA] mt-1">Approximately ½ oz / 14 grams of cotton candy</p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-md" data-testid="spec-shelf-life">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-4">
                <Clock className="text-green-600" size={24} />
              </div>
              <h3 className="font-bold text-[#4A4A4A] mb-2">Shelf Life</h3>
              <p className="text-[#888888]">4–6 week shelf life</p>
              <p className="text-sm text-[#AAAAAA] mt-1">When stored in a climate-controlled environment</p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-md" data-testid="spec-flavors">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                <span className="text-2xl">🍭</span>
              </div>
              <h3 className="font-bold text-[#4A4A4A] mb-2">Flavors</h3>
              <p className="text-[#888888]">Classic wholesale flavors</p>
              <p className="text-sm text-[#AAAAAA] mt-1">Plus dye-free, naturally UV-activated Birthday Cake</p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-md" data-testid="spec-labels">
              <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center mb-4">
                <Tag className="text-yellow-600" size={24} />
              </div>
              <h3 className="font-bold text-[#4A4A4A] mb-2">Label Options</h3>
              <p className="text-[#888888]">Blank, branded, or private-label</p>
              <p className="text-sm text-[#AAAAAA] mt-1">Your branding, our product</p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-md" data-testid="spec-minimum">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                <Building2 className="text-blue-600" size={24} />
              </div>
              <h3 className="font-bold text-[#4A4A4A] mb-2">Order Minimum</h3>
              <p className="text-[#888888]">Boise, ID: 100-unit minimum</p>
              <p className="text-[#888888]">Las Vegas, NV: 500-unit minimum</p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-md" data-testid="spec-delivery">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                <Truck className="text-indigo-600" size={24} />
              </div>
              <h3 className="font-bold text-[#4A4A4A] mb-2">Delivery Areas</h3>
              <p className="text-[#888888]">Las Vegas, NV and Boise, ID</p>
              <p className="text-sm text-[#AAAAAA] mt-1">Monthly delivery available</p>
            </div>
          </div>
        </div>
      </section>

      {/* Ordering Process */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-[#4A4A4A] mb-8 text-center">How Ordering Works</h2>
          
          <div className="space-y-6">
            <div className="flex items-start gap-4 p-6 bg-pink-50 rounded-2xl">
              <div className="w-10 h-10 rounded-full bg-[#FF9EAA] text-white flex items-center justify-center font-bold flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="font-bold text-[#4A4A4A] mb-1">Submit Inquiry</h3>
                <p className="text-[#888888]">Fill out the form below or email us to start the conversation. We&apos;ll discuss your needs and provide pricing.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-6 bg-purple-50 rounded-2xl">
              <div className="w-10 h-10 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="font-bold text-[#4A4A4A] mb-1">Monthly Ordering Window</h3>
                <p className="text-[#888888]">Orders are placed between the 1st–5th of each month via email.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-6 bg-blue-50 rounded-2xl">
              <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="font-bold text-[#4A4A4A] mb-1">Production & Delivery</h3>
                <p className="text-[#888888]">Orders are delivered around the 15th of the month. First-order payment terms available.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="wholesale-form-section">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-[#4A4A4A] mb-2 text-center">Request Wholesale Information</h2>
            <p className="text-[#888888] text-center mb-8">
              Tell us about your business and we&apos;ll get back to you with pricing and availability.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Business & Contact Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="business_name" className="text-[#4A4A4A] font-medium">
                    Business Name *
                  </Label>
                  <Input
                    id="business_name"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleInputChange}
                    required
                    className="mt-2 input-cotton"
                    placeholder="Your Business"
                    data-testid="input-business-name"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_name" className="text-[#4A4A4A] font-medium">
                    Contact Name *
                  </Label>
                  <Input
                    id="contact_name"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleInputChange}
                    required
                    className="mt-2 input-cotton"
                    placeholder="Your Name"
                    data-testid="input-contact-name"
                  />
                </div>
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="text-[#4A4A4A] font-medium">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="mt-2 input-cotton"
                    placeholder="you@business.com"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#4A4A4A] font-medium">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="mt-2 input-cotton"
                    placeholder="(555) 123-4567"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              {/* City */}
              <div>
                <Label htmlFor="city" className="text-[#4A4A4A] font-medium">
                  City *
                </Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className="mt-2 input-cotton"
                  placeholder="Las Vegas, Boise, etc."
                  data-testid="input-city"
                />
              </div>

              {/* Quantity & Delivery Month */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#4A4A4A] font-medium">Estimated Quantity *</Label>
                  <Select onValueChange={(value) => handleSelectChange('estimated_quantity', value)} value={formData.estimated_quantity}>
                    <SelectTrigger className="mt-2 input-cotton" data-testid="select-quantity">
                      <SelectValue placeholder="Select quantity" />
                    </SelectTrigger>
                    <SelectContent>
                      {quantityRanges.map((range) => (
                        <SelectItem key={range} value={range}>{range}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#4A4A4A] font-medium">Requested Delivery Month</Label>
                  <Select onValueChange={(value) => handleSelectChange('delivery_month', value)} value={formData.delivery_month}>
                    <SelectTrigger className="mt-2 input-cotton" data-testid="select-month">
                      <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent>
                      {deliveryMonths.map((month) => (
                        <SelectItem key={month} value={month}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Desired Flavors */}
              <div>
                <Label htmlFor="desired_flavors" className="text-[#4A4A4A] font-medium">
                  Desired Flavors
                </Label>
                <Input
                  id="desired_flavors"
                  name="desired_flavors"
                  value={formData.desired_flavors}
                  onChange={handleInputChange}
                  className="mt-2 input-cotton"
                  placeholder="e.g., Pink Vanilla, Blue Raspberry, UV Birthday Cake..."
                  data-testid="input-flavors"
                />
              </div>

              {/* Label Option */}
              <div>
                <Label className="text-[#4A4A4A] font-medium">Label Preference</Label>
                <Select onValueChange={(value) => handleSelectChange('label_option', value)} value={formData.label_option}>
                  <SelectTrigger className="mt-2 input-cotton" data-testid="select-label">
                    <SelectValue placeholder="Select label option" />
                  </SelectTrigger>
                  <SelectContent>
                    {labelOptions.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-[#4A4A4A] font-medium">
                  Additional Information
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="mt-2 input-cotton min-h-[100px]"
                  placeholder="Tell us about your business, how you plan to sell cotton candy, or any questions you have..."
                  data-testid="input-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#FF9EAA] hover:bg-[#FF8FAB] text-white rounded-full py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all btn-scale"
                data-testid="submit-wholesale-btn"
              >
                {isSubmitting ? "Submitting..." : "Submit Wholesale Inquiry"}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
